'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import AllFiltersModal from './AllFiltersModal';
import type { FilterTrackingContext } from './filterAnalytics';
import { useFilters } from '@/src/lib/filters';

export type OpenAllFiltersOptions = {
  trackingContext?: FilterTrackingContext | null;
  showRoomsSection: boolean;
  showPriceSection: boolean;
};

export const defaultModalOptions: OpenAllFiltersOptions = {
  showRoomsSection: true,
  showPriceSection: true,
};

type AllFiltersModalContextValue = {
  openAllFilters: (options: OpenAllFiltersOptions) => void;
  closeAllFilters: () => void;
};

const AllFiltersModalContext = createContext<AllFiltersModalContextValue | null>(null);

/**
 * Hosts the single All-Filters modal at the persistent search-shell level so it
 * survives the SRP slug morph (which remounts the page-level search bar, and with
 * it any modal owned there). Search bars open it via `useAllFiltersModal()`; the
 * modal edits the URL-driven `FilterProvider` state directly.
 */
export function AllFiltersModalProvider({ children }: { children: ReactNode }) {
  const { filters, setFilters } = useFilters();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [options, setOptions] = useState<OpenAllFiltersOptions>(defaultModalOptions);

  const openAllFilters = useCallback((next: OpenAllFiltersOptions) => {
    setOptions(next);
    setHasOpened(true);
    setIsOpen(true);
  }, []);
  const closeAllFilters = useCallback(() => setIsOpen(false), []);

  const value = useMemo(() => ({ openAllFilters, closeAllFilters }), [openAllFilters, closeAllFilters]);

  return (
    <AllFiltersModalContext.Provider value={value}>
      {children}
      {hasOpened && (
        <AllFiltersModal
          trackingContext={options.trackingContext}
          isOpen={isOpen}
          onClose={closeAllFilters}
          filters={filters}
          setFilters={setFilters}
          showRoomsSection={options.showRoomsSection}
          showPriceSection={options.showPriceSection}
        />
      )}
    </AllFiltersModalContext.Provider>
  );
}

/** Optional — returns null outside the provider (e.g. the homepage hero), so callers can fall back to a local modal. */
export function useAllFiltersModal(): AllFiltersModalContextValue | null {
  return useContext(AllFiltersModalContext);
}
